import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBranches } from '../services/branchService';
import { getProducts } from '../services/productService';
import { getEquipments } from '../services/equipmentService';
import { createTransfer } from '../services/transferService';

const today = () => new Date().toISOString().slice(0, 10);

export default function CrearTraslado() {
  const navigate = useNavigate();

  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const [branchIdOrigin, setBranchIdOrigin] = useState('');
  const [branchIdDest, setBranchIdDest] = useState('');
  const [date, setDate] = useState(today);
  const [observations, setObservations] = useState('');
  const [details, setDetails] = useState([{ kind: 'product', productId: '', equipmentId: '', quantity: '' }]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    Promise.all([getBranches(), getProducts(), getEquipments()])
      .then(([b, p, eq]) => {
        setBranches(Array.isArray(b) ? b.filter(x => x.state) : []);
        setProducts(Array.isArray(p) ? p : []);
        setEquipments(Array.isArray(eq) ? eq : []);
      })
      .catch(() => {})
      .finally(() => setLoadingData(false));
  }, []);

  function handleDetailChange(index, field, value) {
    setDetails(prev => prev.map((d, i) => i === index ? { ...d, [field]: value } : d));
  }

  function addLine() {
    setDetails(prev => [...prev, { kind: 'product', productId: '', equipmentId: '', quantity: '' }]);
  }

  function addEquipmentLine() {
    setDetails(prev => [...prev, { kind: 'equipment', productId: '', equipmentId: '', quantity: '' }]);
  }

  function removeLine(index) {
    setDetails(prev => prev.filter((_, i) => i !== index));
  }

  const totalItems = details.reduce((sum, d) => sum + (Number(d.quantity) || 0), 0);
  const validLines = details.filter(d => (d.kind === 'equipment' ? d.equipmentId : d.productId) && d.quantity);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!validLines.length) {
      setError('Debe agregar al menos un producto con cantidad.');
      return;
    }

    setSaving(true);
    try {
      await createTransfer({
        branchIdOrigin: Number(branchIdOrigin),
        branchIdDest: Number(branchIdDest),
        date,
        observations: observations.trim(),
        details: validLines.map(d => (
          d.kind === 'equipment'
            ? { equipmentId: Number(d.equipmentId), quantity: Number(d.quantity) }
            : { productId: Number(d.productId), quantity: Number(d.quantity) }
        )),
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo crear el traslado.');
    } finally {
      setSaving(false);
    }
  }

  function handleNewTransfer() {
    setBranchIdOrigin('');
    setBranchIdDest('');
    setDate(today);
    setObservations('');
    setDetails([{ kind: 'product', productId: '', equipmentId: '', quantity: '' }]);
    setSuccess(false);
    setError('');
  }

  if (success) {
    return (
      <div className="page-fade" style={{ padding: 24 }}>
        <div className="section-card" style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center' }}>
          <div className="section-body" style={{ padding: '40px 30px' }}>
            <i className="bi bi-check-circle-fill" style={{ fontSize: 48, color: 'var(--orig-green)', display: 'block', marginBottom: 16 }} />
            <h5 style={{ fontWeight: 700, color: 'var(--orig-text)', fontFamily: 'var(--orig-font-display)', marginBottom: 8 }}>
              Traslado creado
            </h5>
            <p style={{ fontSize: 13, color: 'var(--orig-text-muted)', marginBottom: 24 }}>
              El traslado fue registrado exitosamente.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button className="btn-orig btn-orig-green" onClick={handleNewTransfer}>
                <i className="bi bi-plus-lg" />
                Nuevo Traslado
              </button>
              <button className="btn-orig btn-orig-outline" onClick={() => navigate('/traslados')}>
                <i className="bi bi-list-ul" />
                Ver Traslados
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-fade" style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <p style={{ fontSize: 12, color: 'var(--orig-text-muted)', marginBottom: 3 }}>
          Registrar movimiento de inventario entre sucursales
        </p>
        <h5 style={{
          fontWeight: 800, color: 'var(--orig-text)',
          fontFamily: 'var(--orig-font-display)', letterSpacing: 0.5, fontSize: 20,
        }}>
          Nuevo Traslado
        </h5>
      </div>

      {loadingData ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
          <i className="bi bi-arrow-repeat" style={{ fontSize: 28, display: 'block', marginBottom: 10, animation: 'spin 1s linear infinite' }} />
          Cargando datos...
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {/* Info general */}
          <div className="section-card" style={{ marginBottom: 20 }}>
            <div className="section-header">
              <h6>
                <i className="bi bi-info-circle" style={{ color: 'var(--orig-green)' }} />
                Información del traslado
              </h6>
            </div>
            <div className="section-body">
              {error && (
                <div className="alert-orig alert-error" style={{ marginBottom: 16 }}>
                  <i className="bi bi-exclamation-triangle-fill" />
                  {error}
                </div>
              )}

              <div className="row g-3">
                <div className="col-12 col-md-5">
                  <label className="form-label">Sucursal Origen</label>
                  <select
                    className="form-control"
                    value={branchIdOrigin}
                    onChange={e => setBranchIdOrigin(e.target.value)}
                    required
                  >
                    <option value="">Seleccione origen</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-2" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 6 }}>
                  <i className="bi bi-arrow-right" style={{ fontSize: 22, color: 'var(--orig-green)' }} />
                </div>

                <div className="col-12 col-md-5">
                  <label className="form-label">Sucursal Destino</label>
                  <select
                    className="form-control"
                    value={branchIdDest}
                    onChange={e => setBranchIdDest(e.target.value)}
                    required
                  >
                    <option value="">Seleccione destino</option>
                    {branches.filter(b => String(b.id) !== String(branchIdOrigin)).map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label">Fecha</label>
                  <input
                    type="date"
                    className="form-control"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    required
                  />
                </div>

                <div className="col-12 col-md-8">
                  <label className="form-label">
                    Observaciones <span style={{ opacity: 0.5, fontSize: '0.8em' }}>(opcional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: Sin novedad"
                    value={observations}
                    onChange={e => setObservations(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Detalle de productos y equipos */}
          <div className="section-card" style={{ marginBottom: 20 }}>
            <div className="section-header">
              <h6>
                <i className="bi bi-box-seam" style={{ color: 'var(--orig-green)' }} />
                Productos y Equipos
              </h6>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {validLines.length > 0 && (
                  <span className="badge-orig badge-green">
                    {validLines.length} {validLines.length === 1 ? 'línea' : 'líneas'} · {totalItems} unidades
                  </span>
                )}
                <button type="button" className="btn-orig btn-orig-green" onClick={addLine} style={{ padding: '5px 12px', fontSize: 12 }}>
                  <i className="bi bi-plus-lg" />
                  Agregar línea
                </button>
                <button type="button" className="btn-orig btn-orig-outline" onClick={addEquipmentLine} style={{ padding: '5px 12px', fontSize: 12 }}>
                  <i className="bi bi-pc-display" />
                  Agregar Equipos
                </button>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="orig-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Producto / Equipo</th>
                    <th style={{ width: 130 }}>Cantidad</th>
                    <th style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {details.map((line, idx) => (
                    <tr key={idx}>
                      <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>
                        {idx + 1}
                      </td>
                      <td>
                        {line.kind === 'equipment' ? (
                          <select
                            className="form-control"
                            value={line.equipmentId}
                            onChange={e => handleDetailChange(idx, 'equipmentId', e.target.value)}
                            required
                            style={{ marginBottom: 0 }}
                          >
                            <option value="">Seleccione un equipo</option>
                            {equipments.map(eq => (
                              <option key={eq.id} value={eq.id}>
                                {eq.name}{eq.type ? ` — ${eq.type}` : ''}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <select
                            className="form-control"
                            value={line.productId}
                            onChange={e => handleDetailChange(idx, 'productId', e.target.value)}
                            required
                            style={{ marginBottom: 0 }}
                          >
                            <option value="">Seleccione un producto</option>
                            {products.map(p => (
                              <option key={p.id} value={p.id}>
                                {p.name} — {p.packageType}{p.packageQuantity ? ` × ${p.packageQuantity}` : ''}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="0"
                          min="1"
                          value={line.quantity}
                          onChange={e => handleDetailChange(idx, 'quantity', e.target.value)}
                          required
                          style={{ marginBottom: 0 }}
                        />
                      </td>
                      <td>
                        {details.length > 1 && (
                          <button
                            type="button"
                            className="btn-orig btn-orig-outline"
                            onClick={() => removeLine(idx)}
                            style={{ padding: '5px 10px', color: 'var(--orig-red-light)' }}
                            title="Eliminar línea"
                          >
                            <i className="bi bi-trash" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {details.length === 0 && (
                <div style={{ padding: 30, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
                  <div style={{ fontSize: 13, marginBottom: 8 }}>No hay productos agregados</div>
                  <button type="button" className="btn-orig btn-orig-outline" onClick={addLine} style={{ fontSize: 12 }}>
                    <i className="bi bi-plus-lg" />
                    Agregar producto
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn-orig btn-orig-outline" onClick={() => navigate('/traslados')}>
              Cancelar
            </button>
            <button type="submit" className="btn-orig btn-orig-green" disabled={saving}>
              <i className="bi bi-send" />
              {saving ? 'Registrando...' : 'Registrar Traslado'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
